import { Link } from "react-router-dom";
import { useSettings } from "@/contexts/SettingsContext";
import { useCategories } from "@/contexts/CategoriesContext";
import { resolveImageUrl } from "@/lib/api";
import { Instagram, Twitter, Facebook } from "lucide-react";

export default function Footer() {
  const { settings: s } = useSettings();
  const { categories } = useCategories();
  return (
    <footer className="mt-8 lg:mt-12 border-t border-border bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-2">
            {s.logo_url ? (
              <img src={resolveImageUrl(s.logo_url)} alt={s.site_name} className="h-7 w-auto max-w-[100px] object-contain" />
            ) : (
              <span className="w-6 h-6 rounded-sm bg-foreground grid place-items-center">
                <span className="block w-2 h-2 rounded-full bg-[hsl(var(--accent))]" />
              </span>
            )}
            <span className="font-display font-semibold">{s.site_name || "PeptideSeries"}</span>
          </div>
          <p className="mt-4 text-sm text-muted-foreground max-w-xs">
            {s.footer_note}
          </p>
          {(s.contact_email || s.contact_phone) && (
            <p className="mt-3 text-xs text-muted-foreground">
              {s.contact_email && <span>{s.contact_email}</span>}
              {s.contact_email && s.contact_phone && " · "}
              {s.contact_phone && <span>{s.contact_phone}</span>}
            </p>
          )}
          {s.address && (
            <p className="mt-2 text-xs text-muted-foreground whitespace-pre-line">{s.address}</p>
          )}
          {(s.social_instagram || s.social_twitter || s.social_facebook) && (
            <div className="mt-4 flex gap-2">
              {s.social_instagram && (
                <a href={s.social_instagram} target="_blank" rel="noopener noreferrer"
                   data-testid="social-instagram"
                   className="w-8 h-8 grid place-items-center border border-border hover:bg-secondary">
                  <Instagram className="w-4 h-4" />
                </a>
              )}
              {s.social_twitter && (
                <a href={s.social_twitter} target="_blank" rel="noopener noreferrer"
                   data-testid="social-twitter"
                   className="w-8 h-8 grid place-items-center border border-border hover:bg-secondary">
                  <Twitter className="w-4 h-4" />
                </a>
              )}
              {s.social_facebook && (
                <a href={s.social_facebook} target="_blank" rel="noopener noreferrer"
                   data-testid="social-facebook"
                   className="w-8 h-8 grid place-items-center border border-border hover:bg-secondary">
                  <Facebook className="w-4 h-4" />
                </a>
              )}
            </div>
          )}
        </div>

        <div>
          <p className="overline mb-4">Catalog</p>
          <ul className="space-y-2 text-sm">
            {categories.length === 0 && (
              <li className="text-muted-foreground">—</li>
            )}
            {categories.map((c) => (
              <li key={c.category_id}>
                <Link to={`/catalog?category=${c.slug}`} className="hover:text-[hsl(var(--accent))]">
                  {c.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="overline mb-4">Access</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/register" className="hover:text-[hsl(var(--accent))]">Doctor sign-up</Link></li>
            <li><Link to="/login" className="hover:text-[hsl(var(--accent))]">Sign in</Link></li>
            <li><Link to="/about" className="hover:text-[hsl(var(--accent))]">Quality assurance</Link></li>
          </ul>
        </div>

        <div>
          <p className="overline mb-4">Legal</p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>For research & clinically-supervised use only.</li>
            <li>Doctors only. ID + medical license required for ordering.</li>
            <li>© {new Date().getFullYear()} {s.site_name || "PeptideSeries"}.</li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
